// MpesaInstructions — step-by-step M-Pesa Paybill instructions for one pending payment.
// Single home of the instruction steps: used after enrolling (CoursesPage) and on pending
// rows (PaymentsPage). Paybill, account format and support phone come from system_config
// (category mpesa_config) via useConfig — nothing about the business account is hardcoded here.
import { useState, type ReactNode } from "react";
import { useConfig } from "../../hooks/useConfig.js";
import { patchJson } from "../../hooks/useApi.js";
import { formatAccountReference, isConfigFlagEnabled, normaliseProofReference } from "@dm-lms/shared/utils";
import {
  DEFAULT_ACCOUNT_REF_FORMAT,
  MPESA_CONFIG_KEYS,
  MPESA_STK_KEY,
  PAYMENT_REFERENCE_LENGTH,
} from "@dm-lms/shared/constants";
import type { Payment } from "@dm-lms/shared/types";
import ViewCoursesLink from "./ViewCoursesLink.js";

interface MpesaInstructionsProps {
  paymentId:           string;
  amount:              number;          // KES
  paymentCode:         string;          // PAY-XXXX
  courseAccountRef:    string | null;   // course prefix (accountRef ?? code); null → payment code alone
  initialReference?:   string | null;   // transaction code the student already submitted, if any
  verificationFailed?: boolean;         // admin checked the submitted code and it did not match
  paid?:               boolean;         // payment confirmed — the card becomes a success message
  onSubmitted?:        () => void;      // after a code is saved, so the caller can refresh its data
  onDismiss?:          () => void;
}

const CARD_CLASS = "rounded-lg border border-gray-200 bg-white p-4 text-sm";

function PhoneIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-5 w-5 text-dm-navy"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="7" y="2" width="10" height="20" rx="2" />
      <path d="M11 18h2" />
    </svg>
  );
}

function CheckIcon({ className }: { className: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

interface PaymentCodeSectionProps {
  paymentId:          string;
  initialReference:   string | null;
  verificationFailed: boolean;
  onSubmitted:        (() => void) | undefined;
}

// "Already paid?" — the one place a student's transaction code is typed and submitted.
function PaymentCodeSection({ paymentId, initialReference, verificationFailed, onSubmitted }: PaymentCodeSectionProps) {
  const [submitted, setSubmitted] = useState<string | null>(null);   // set once this session saves a code
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reference = submitted ?? initialReference;
  const showInput = reference === null || editing;

  async function submit() {
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await patchJson<Payment>(`/api/payments/${paymentId}/reference`, { reference: input });
      if (!res.ok) {
        setError(res.error);
        return;
      }
      setSubmitted(res.data.reference ?? input);
      setEditing(false);
      setInput("");
      onSubmitted?.();
    } catch {
      setError("Unable to submit code. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      <h4 className="font-medium text-dm-navy">Already paid?</h4>
      <p className="mt-1 text-dm-slate">Enter your M-Pesa transaction code so our team can verify your payment.</p>

      {verificationFailed && submitted === null && (
        <p className="mt-2 text-red-600">
          Your payment code could not be verified. Please check your M-Pesa code and resubmit, or contact support.
        </p>
      )}

      {reference !== null && !editing && (
        <p className="mt-2 flex flex-wrap items-center gap-2 text-green-700">
          <CheckIcon className="h-4 w-4" />
          <span>
            Code submitted: <strong className="font-mono">{reference}</strong>
          </span>
          <button
            type="button"
            onClick={() => { setEditing(true); }}
            className="text-xs text-dm-royal underline-offset-2 hover:underline"
          >
            Change code
          </button>
        </p>
      )}

      {showInput && (
        <form
          className="mt-2"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <div className="flex flex-wrap gap-2">
            <input
              type="text"
              value={input}
              maxLength={PAYMENT_REFERENCE_LENGTH.max}
              placeholder="e.g. QGH7X8Y9Z0"
              aria-label="M-Pesa transaction code"
              onChange={(event) => { setInput(normaliseProofReference(event.target.value)); }}
              className="w-40 sm:w-48 rounded-md border border-gray-300 px-3 py-2 font-mono text-sm text-dm-navy focus:border-dm-royal focus:outline-none focus:ring-1 focus:ring-dm-royal"
            />
            <button
              type="submit"
              disabled={input.length < PAYMENT_REFERENCE_LENGTH.min || isSubmitting}
              className="rounded-md bg-dm-navy px-4 py-2 text-sm font-medium text-white transition hover:bg-dm-blue disabled:opacity-50"
            >
              {isSubmitting ? "Submitting…" : "Submit code"}
            </button>
            {editing && (
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  setEditing(false);
                  setInput("");
                  setError(null);
                }}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-dm-slate transition hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
            )}
          </div>
          {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </form>
      )}
    </div>
  );
}

function Header() {
  return (
    <div className="mb-3 flex items-center gap-2">
      <PhoneIcon />
      <h3 className="font-medium text-dm-navy">Pay via M-Pesa</h3>
    </div>
  );
}

export default function MpesaInstructions({
  paymentId,
  amount,
  paymentCode,
  courseAccountRef,
  initialReference = null,
  verificationFailed = false,
  paid = false,
  onSubmitted,
  onDismiss,
}: MpesaInstructionsProps) {
  const { options, loading } = useConfig("mpesa_config");
  const configValue = (key: string) => options.find((option) => option.key === key)?.label;

  // Nothing left to pay, so no instructions (or config) are needed — just the confirmation.
  if (paid) {
    return (
      <section className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm" aria-label="Payment confirmed">
        <div className="flex items-center gap-2 text-green-700">
          <CheckIcon className="h-5 w-5" />
          <p className="font-medium">Payment confirmed! Thank you for your payment.</p>
        </div>
        <div className="mt-3">
          <ViewCoursesLink />
        </div>
      </section>
    );
  }

  if (loading) {
    return (
      <div className={`${CARD_CLASS} animate-pulse`} aria-busy="true" aria-label="Loading payment instructions">
        <div className="mb-4 h-5 w-32 rounded bg-gray-200" />
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="mb-2 h-4 w-3/4 rounded bg-gray-200" />
        ))}
      </div>
    );
  }

  const paybill = configValue(MPESA_CONFIG_KEYS.paybill);
  const supportPhone = configValue(MPESA_CONFIG_KEYS.supportPhone);
  // Admin switch (System Config → M-Pesa). GET /api/config returns active rows only, so a deactivated
  // or missing stk_push_enabled row reads as off and the manual instructions below are all that shows.
  const stkEnabled = isConfigFlagEnabled(configValue(MPESA_STK_KEY));
  const accountNumber = formatAccountReference(
    configValue(MPESA_CONFIG_KEYS.accountRefFormat) ?? DEFAULT_ACCOUNT_REF_FORMAT,
    courseAccountRef,
    paymentCode
  );
  const formattedAmount = amount.toLocaleString();

  const dismissButton = onDismiss && (
    <button
      type="button"
      onClick={onDismiss}
      className="mt-4 rounded-md bg-dm-navy px-4 py-2 text-sm font-medium text-white transition hover:bg-dm-blue"
    >
      Done
    </button>
  );

  // Without a business number the steps would be unusable, and guessing one could send money
  // to the wrong account — so show the reference and amount we do know, and ask them to call.
  if (!paybill) {
    return (
      <section className={CARD_CLASS} aria-label="M-Pesa payment instructions">
        <Header />
        <p className="text-dm-slate">
          Payment details are temporarily unavailable. Your payment reference is{" "}
          <strong className="text-dm-navy">{accountNumber}</strong> for KES {formattedAmount}.
          {supportPhone ? ` Please call ${supportPhone} to complete payment.` : " Please contact support to complete payment."}
        </p>
        {dismissButton}
      </section>
    );
  }

  const steps: ReactNode[] = [
    "Open M-Pesa on your phone",
    "Select Lipa na M-Pesa → Paybill",
    <>Enter Business number: <strong className="text-dm-navy">{paybill}</strong></>,
    <>Enter Account number: <strong className="text-dm-navy">{accountNumber}</strong></>,
    <>Enter Amount: <strong className="text-dm-navy">KES {formattedAmount}</strong></>,
    "Enter your M-Pesa PIN and confirm",
  ];

  return (
    <section className={CARD_CLASS} aria-label="M-Pesa payment instructions">
      <Header />

      {stkEnabled && (
        // TODO (Milestone 2): render the STK push button here.
        // When Daraja is ready, show a "Pay now — send to my phone" button that starts the STK push via
        // POST /api/payments/mpesa (today a [USER] stub in routes/payments). The manual steps below stay
        // as the fallback.
        null
      )}

      <ol className="list-decimal space-y-1 pl-5 text-dm-slate">
        {steps.map((step, index) => (
          <li key={index}>{step}</li>
        ))}
      </ol>

      <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800">
        Send exactly KES {formattedAmount}. Your account will be activated once payment is confirmed by our team.
      </div>

      <PaymentCodeSection
        paymentId={paymentId}
        initialReference={initialReference}
        verificationFailed={verificationFailed}
        onSubmitted={onSubmitted}
      />

      {supportPhone && <p className="mt-3 text-dm-slate">Need help? Call {supportPhone}</p>}

      {dismissButton}
    </section>
  );
}
